import { Eye } from "lucide-react";
import { useAssets } from "@/contexts/AssetContext.jsx";
import { Badge } from "@/components/ui/badge.jsx";
import { Button } from "@/components/ui/button.jsx";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.jsx";
import { cardStyles, outlineChromeButton } from "@/lib/status-styles.js";
import { cn } from "@/lib/utils.js";
import {
  getStatusBadgeClass,
  getStatusText,
  getStatusDotClass,
} from "@/lib/status-utils.js";
import { getDateOffset, formatTimestamp } from "@/lib/datetime-utils.js";
import {
  calculateRuntimePercentage,
  calculateMechanicalAvailabilityPercentage,
} from "@/lib/downtime-data-utils.js";
import { formatAvailabilityPercent } from "@/lib/no-data-utils.js";
import { LoadingSkeleton } from "@/components/ui/loading.jsx";

/**
 * Column descriptor array for the downtime table.
 * Each entry has renderHead(ctx) returning a TableHead and renderCell(compressor, ctx) returning a TableCell.
 * @type {Array<{ key: string, label: string, locked?: boolean, defaultVisible: boolean, sortKey?: string, renderHead: Function, renderCell: Function }>}
 */
export const DOWNTIME_COLUMNS = [
  {
    key: "assetNumber",
    label: "Asset Number",
    locked: true,
    defaultVisible: true,
    sortKey: "assetNumber",
    renderHead: (ctx) => (
      <TableHead
        key='assetNumber'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("assetNumber")}
        data-testid='header-assetnumber'>
        <div className='flex items-center'>
          Asset Number
          {ctx.getSortIcon?.("assetNumber")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor, ctx) => {
      const asset =
        compressor.assetId != null
          ? ctx.assets?.find((a) => String(a.id) === String(compressor.assetId))
          : undefined;
      const isOptimizationEnabled = asset?.optimizationEnabled;
      return (
        <TableCell
          key='assetNumber'
          className={`text-sm text-foreground font-normal py-3 px-4 relative ${isOptimizationEnabled ? "pl-5" : ""}`}>
          {isOptimizationEnabled && (
            <div
              className='absolute left-0 top-0 bottom-0 w-[3px] bg-brand-cyan z-0'
              aria-hidden
            />
          )}
          <span className='relative z-0'>{compressor.assetNumber}</span>
        </TableCell>
      );
    },
  },
  {
    key: "assetLease",
    label: "Lease Name",
    defaultVisible: true,
    sortKey: "assetLease",
    renderHead: (ctx) => (
      <TableHead
        key='assetLease'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("assetLease")}
        data-testid='header-lease'>
        <div className='flex items-center'>
          Lease Name
          {ctx.getSortIcon?.("assetLease")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor) => (
      <TableCell
        key='assetLease'
        className='text-sm text-foreground font-normal py-3 px-4'>
        {compressor.assetLease}
      </TableCell>
    ),
  },
  {
    key: "vendor",
    label: "Vendor",
    defaultVisible: true,
    sortKey: "vendor",
    renderHead: (ctx) => (
      <TableHead
        key='vendor'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("vendor")}
        data-testid='header-vendor'>
        <div className='flex items-center'>
          Vendor
          {ctx.getSortIcon?.("vendor")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor) => (
      <TableCell key='vendor' className='text-sm text-foreground font-normal py-3 px-4'>
        {compressor.vendor}
      </TableCell>
    ),
  },
  {
    key: "status",
    label: "Status",
    defaultVisible: true,
    sortKey: "status",
    renderHead: (ctx) => (
      <TableHead
        key='status'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("status")}
        data-testid='header-status'>
        <div className='flex items-center'>
          Status
          {ctx.getSortIcon?.("status")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor) => (
      <TableCell key='status'>
        <Badge
          variant='status'
          className={getStatusBadgeClass(compressor.status)}
          data-testid={`badge-status-${compressor.assetNumber}`}>
          <div
            className={getStatusDotClass(
              compressor.status,
              "w-1.5 h-1.5 rounded-full"
            )}
          />
          {getStatusText(compressor.status)}
        </Badge>
      </TableCell>
    ),
  },
  {
    key: "totalDTHours",
    label: "Downtime",
    defaultVisible: true,
    sortKey: "totalDTHours",
    renderHead: (ctx) => (
      <TableHead
        key='totalDTHours'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("totalDTHours")}
        data-testid='header-downtime'>
        <div className='flex items-center'>
          Downtime
          {ctx.getSortIcon?.("totalDTHours")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor) => (
      <TableCell
        key='totalDTHours'
        className='text-sm text-foreground font-normal py-3 px-4'>
        <span
          className='text-sm font-mono font-normal'
          data-testid={`text-dthours-${compressor.assetNumber}`}>
          {compressor.totalDTHours.toFixed(1)} h
        </span>
      </TableCell>
    ),
  },
  {
    key: "runtime",
    label: "Runtime",
    defaultVisible: true,
    sortKey: "runtime",
    renderHead: (ctx) => (
      <TableHead
        key='runtime'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("runtime")}
        data-testid='header-runtime'>
        <div className='flex items-center'>
          Runtime
          {ctx.getSortIcon?.("runtime")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor, ctx) => (
      <TableCell key='runtime' className='text-sm text-foreground font-normal py-3 px-4'>
        <div className='flex items-center gap-1'>
          <span
            className='text-sm font-mono font-normal'
            data-testid={`text-runtime-${compressor.assetNumber}`}>
            {ctx.displayRuntimePercentage(compressor.totalDTHours)}%
          </span>
        </div>
      </TableCell>
    ),
  },
  {
    key: "mechanicalAvailability",
    label: "Mechanical Availability",
    defaultVisible: true,
    sortKey: "mechanicalAvailability",
    renderHead: (ctx) => (
      <TableHead
        key='mechanicalAvailability'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("mechanicalAvailability")}
        data-testid='header-mechanical-availability'>
        <div className='flex items-center'>
          Mechanical Availability
          {ctx.getSortIcon?.("mechanicalAvailability")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor, ctx) => (
      <TableCell
        key='mechanicalAvailability'
        className='text-sm text-foreground font-normal py-3 px-4'>
        <div className='flex items-center gap-1'>
          <span
            className='text-sm font-mono font-normal'
            data-testid={`text-mechanical-availability-${compressor.assetNumber}`}>
            {ctx.displayMechanicalAvailabilityPercentage(
              compressor.mechanicalDTHours || 0
            )}
            %
          </span>
        </div>
      </TableCell>
    ),
  },
  {
    key: "communicationsAvailability",
    label: "Communications Availability (%)",
    defaultVisible: true,
    sortKey: "communicationsAvailability",
    renderHead: (ctx) => (
      <TableHead
        key='communicationsAvailability'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("communicationsAvailability")}
        data-testid='header-communications-availability'>
        <div className='flex items-center'>
          Communications Availability (%)
          {ctx.getSortIcon?.("communicationsAvailability")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor, ctx) => (
      <TableCell
        key='communicationsAvailability'
        className='text-sm text-foreground font-normal py-3 px-4'>
        {ctx.noDataStatsLoading ? (
          <LoadingSkeleton className='h-4 w-14 rounded' />
        ) : (
          <span
            className='text-sm font-mono font-normal'
            data-testid={`text-communications-availability-${compressor.assetNumber}`}>
            {formatAvailabilityPercent(
              compressor.communicationsAvailabilityPercent
            )}
          </span>
        )}
      </TableCell>
    ),
  },
  {
    key: "lastDowntimeReason",
    label: "Last Downtime Reason",
    defaultVisible: true,
    renderHead: () => (
      <TableHead key='lastDowntimeReason' className={cardStyles.tableHead}>
        Last Downtime Reason
      </TableHead>
    ),
    renderCell: (compressor, ctx) => {
      const latestEvent = ctx.getLatestEvent(compressor.events);
      return (
        <TableCell
          key='lastDowntimeReason'
          className='text-sm text-foreground font-normal py-3 px-4'>
          {latestEvent ? (
            <div
              className='text-sm font-normal truncate'
              data-testid={`text-reason-${compressor.assetNumber}`}
              title={latestEvent.relatedAlarm}>
              {latestEvent.relatedAlarm}
            </div>
          ) : (
            "-"
          )}
        </TableCell>
      );
    },
  },
  {
    key: "lastDowntimeTime",
    label: "Time Since Last Downtime Reason",
    defaultVisible: true,
    sortKey: "lastDowntimeTime",
    renderHead: (ctx) => (
      <TableHead
        key='lastDowntimeTime'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("lastDowntimeTime")}
        data-testid='header-time-since-downtime'>
        <div className='flex items-center'>
          Time Since Last Downtime Reason
          {ctx.getSortIcon?.("lastDowntimeTime")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor, ctx) => {
      const latestEvent = ctx.getLatestEvent(compressor.events);
      return (
        <TableCell
          key='lastDowntimeTime'
          className='text-sm text-foreground font-normal py-3 px-4'>
          {latestEvent?.dateTime ? formatTimestamp(latestEvent.dateTime) : "-"}
        </TableCell>
      );
    },
  },
  {
    key: "eventCount",
    label: "Events",
    defaultVisible: true,
    sortKey: "eventCount",
    renderHead: (ctx) => (
      <TableHead
        key='eventCount'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("eventCount")}
        data-testid='header-events'>
        <div className='flex items-center'>
          Events
          {ctx.getSortIcon?.("eventCount")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor) => (
      <TableCell
        key='eventCount'
        className='text-sm text-foreground font-normal py-3 px-4'>
        <span
          className='text-sm font-mono font-medium'
          data-testid={`text-events-${compressor.assetNumber}`}>
          {compressor.eventCount}
        </span>
      </TableCell>
    ),
  },
  // Optional / default-off columns
  {
    key: "assetLocation",
    label: "Asset Location",
    defaultVisible: false,
    renderHead: () => (
      <TableHead key='assetLocation' className={cardStyles.tableHead}>
        Asset Location
      </TableHead>
    ),
    renderCell: (compressor) => (
      <TableCell
        key='assetLocation'
        className='text-sm text-foreground font-normal py-3 px-4'>
        {compressor.assetLocation || "-"}
      </TableCell>
    ),
  },
  {
    key: "mechanicalDTHours",
    label: "Mechanical DT Hours",
    defaultVisible: false,
    sortKey: "mechanicalDTHours",
    renderHead: (ctx) => (
      <TableHead
        key='mechanicalDTHours'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("mechanicalDTHours")}
        data-testid='header-mechanical-dt-hours'>
        <div className='flex items-center'>
          Mechanical DT Hours
          {ctx.getSortIcon?.("mechanicalDTHours")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor) => (
      <TableCell
        key='mechanicalDTHours'
        className='text-sm text-foreground font-normal py-3 px-4'>
        <span className='text-sm font-mono font-normal'>
          {(compressor.mechanicalDTHours || 0).toFixed(1)} h
        </span>
      </TableCell>
    ),
  },
  {
    key: "nonMechanicalDTHours",
    label: "Non-Mechanical DT Hours",
    defaultVisible: false,
    sortKey: "nonMechanicalDTHours",
    renderHead: (ctx) => (
      <TableHead
        key='nonMechanicalDTHours'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("nonMechanicalDTHours")}
        data-testid='header-non-mechanical-dt-hours'>
        <div className='flex items-center'>
          Non-Mechanical DT Hours
          {ctx.getSortIcon?.("nonMechanicalDTHours")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor) => (
      <TableCell
        key='nonMechanicalDTHours'
        className='text-sm text-foreground font-normal py-3 px-4'>
        <span className='text-sm font-mono font-normal'>
          {(compressor.nonMechanicalDTHours || 0).toFixed(1)} h
        </span>
      </TableCell>
    ),
  },
  // Asset-context columns (retrieved via assetId → AssetContext lookup)
  {
    key: "telemetrySource",
    label: "Telemetry Source",
    defaultVisible: false,
    sortKey: "telemetrySource",
    renderHead: (ctx) => (
      <TableHead
        key='telemetrySource'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("telemetrySource")}>
        <div className='flex items-center'>
          Telemetry Source
          {ctx.getSortIcon?.("telemetrySource")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor, ctx) => {
      const asset = ctx.assets?.find((a) => String(a.id) === String(compressor.assetId));
      return (
        <TableCell key='telemetrySource' className='text-sm text-foreground font-normal py-3 px-4 capitalize'>
          {asset?.telemetrySource || "-"}
        </TableCell>
      );
    },
  },
  {
    key: "optimizationEnabled",
    label: "Optimization Enabled",
    defaultVisible: false,
    sortKey: "optimizationEnabled",
    renderHead: (ctx) => (
      <TableHead
        key='optimizationEnabled'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("optimizationEnabled")}>
        <div className='flex items-center'>
          Optimization Enabled
          {ctx.getSortIcon?.("optimizationEnabled")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor, ctx) => {
      const asset = ctx.assets?.find((a) => String(a.id) === String(compressor.assetId));
      return (
        <TableCell key='optimizationEnabled' className='text-sm text-foreground font-normal py-3 px-4'>
          <Badge variant='secondary' className='text-xs font-medium bg-muted text-muted-foreground border border-border'>
            {asset?.optimizationEnabled ? "Yes" : "No"}
          </Badge>
        </TableCell>
      );
    },
  },
  {
    key: "lastFault",
    label: "Last Fault",
    defaultVisible: false,
    sortKey: "lastFault",
    renderHead: (ctx) => (
      <TableHead
        key='lastFault'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("lastFault")}>
        <div className='flex items-center'>
          Last Fault
          {ctx.getSortIcon?.("lastFault")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor, ctx) => {
      const asset = ctx.assets?.find((a) => String(a.id) === String(compressor.assetId));
      return (
        <TableCell key='lastFault' className='text-sm text-foreground font-normal py-3 px-4'>
          {asset?.lastFault || "-"}
        </TableCell>
      );
    },
  },
  {
    key: "lastFaultTime",
    label: "Last Fault Time",
    defaultVisible: false,
    sortKey: "lastFaultTime",
    renderHead: (ctx) => (
      <TableHead
        key='lastFaultTime'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("lastFaultTime")}>
        <div className='flex items-center'>
          Last Fault Time
          {ctx.getSortIcon?.("lastFaultTime")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor, ctx) => {
      const asset = ctx.assets?.find((a) => String(a.id) === String(compressor.assetId));
      return (
        <TableCell key='lastFaultTime' className='text-sm text-foreground font-normal py-3 px-4'>
          {asset?.lastFaultTime ? formatTimestamp(asset.lastFaultTime) : "-"}
        </TableCell>
      );
    },
  },
  {
    key: "lastUpdated",
    label: "Last Updated",
    defaultVisible: false,
    sortKey: "lastUpdated",
    renderHead: (ctx) => (
      <TableHead
        key='lastUpdated'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("lastUpdated")}>
        <div className='flex items-center'>
          Last Updated
          {ctx.getSortIcon?.("lastUpdated")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor, ctx) => {
      const asset = ctx.assets?.find((a) => String(a.id) === String(compressor.assetId));
      return (
        <TableCell key='lastUpdated' className='text-sm text-foreground font-normal py-3 px-4'>
          {asset?.lastUpdated ? formatTimestamp(asset.lastUpdated) : "-"}
        </TableCell>
      );
    },
  },
  {
    key: "foreman",
    label: "Foreman",
    defaultVisible: false,
    sortKey: "foreman",
    renderHead: (ctx) => (
      <TableHead
        key='foreman'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("foreman")}>
        <div className='flex items-center'>
          Foreman
          {ctx.getSortIcon?.("foreman")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor, ctx) => {
      const asset = ctx.assets?.find((a) => String(a.id) === String(compressor.assetId));
      return (
        <TableCell key='foreman' className='text-sm text-foreground font-normal py-3 px-4'>
          {asset?.foreman || "-"}
        </TableCell>
      );
    },
  },
  {
    key: "customer",
    label: "Customer",
    defaultVisible: false,
    sortKey: "customer",
    renderHead: (ctx) => (
      <TableHead
        key='customer'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("customer")}>
        <div className='flex items-center'>
          Customer
          {ctx.getSortIcon?.("customer")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor, ctx) => {
      const asset = ctx.assets?.find((a) => String(a.id) === String(compressor.assetId));
      return (
        <TableCell key='customer' className='text-sm text-foreground font-normal py-3 px-4'>
          {asset?.customer || "-"}
        </TableCell>
      );
    },
  },
  {
    key: "horsepower",
    label: "Horsepower",
    defaultVisible: false,
    sortKey: "horsepower",
    renderHead: (ctx) => (
      <TableHead
        key='horsepower'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("horsepower")}>
        <div className='flex items-center'>
          Horsepower
          {ctx.getSortIcon?.("horsepower")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor, ctx) => {
      const asset = ctx.assets?.find((a) => String(a.id) === String(compressor.assetId));
      return (
        <TableCell key='horsepower' className='text-sm text-foreground font-normal py-3 px-4'>
          {asset?.horsepower != null ? asset.horsepower : "-"}
        </TableCell>
      );
    },
  },
  {
    key: "compressorModel",
    label: "Compressor Model",
    defaultVisible: false,
    sortKey: "compressorModel",
    renderHead: (ctx) => (
      <TableHead
        key='compressorModel'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("compressorModel")}>
        <div className='flex items-center'>
          Compressor Model
          {ctx.getSortIcon?.("compressorModel")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor, ctx) => {
      const asset = ctx.assets?.find((a) => String(a.id) === String(compressor.assetId));
      return (
        <TableCell key='compressorModel' className='text-sm text-foreground font-normal py-3 px-4'>
          {asset?.compressorModel || "-"}
        </TableCell>
      );
    },
  },
  {
    key: "engineModel",
    label: "Engine Model",
    defaultVisible: false,
    sortKey: "engineModel",
    renderHead: (ctx) => (
      <TableHead
        key='engineModel'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("engineModel")}>
        <div className='flex items-center'>
          Engine Model
          {ctx.getSortIcon?.("engineModel")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor, ctx) => {
      const asset = ctx.assets?.find((a) => String(a.id) === String(compressor.assetId));
      return (
        <TableCell key='engineModel' className='text-sm text-foreground font-normal py-3 px-4'>
          {asset?.engineModel || "-"}
        </TableCell>
      );
    },
  },
  {
    key: "latitude",
    label: "Latitude",
    defaultVisible: false,
    sortKey: "latitude",
    renderHead: (ctx) => (
      <TableHead
        key='latitude'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("latitude")}>
        <div className='flex items-center'>
          Latitude
          {ctx.getSortIcon?.("latitude")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor, ctx) => {
      const asset = ctx.assets?.find((a) => String(a.id) === String(compressor.assetId));
      return (
        <TableCell key='latitude' className='text-sm text-foreground font-normal py-3 px-4 font-mono text-sm'>
          {asset?.latitude != null ? asset.latitude : "-"}
        </TableCell>
      );
    },
  },
  {
    key: "longitude",
    label: "Longitude",
    defaultVisible: false,
    sortKey: "longitude",
    renderHead: (ctx) => (
      <TableHead
        key='longitude'
        className={cardStyles.tableHead}
        onClick={() => ctx.onSort?.("longitude")}>
        <div className='flex items-center'>
          Longitude
          {ctx.getSortIcon?.("longitude")}
        </div>
      </TableHead>
    ),
    renderCell: (compressor, ctx) => {
      const asset = ctx.assets?.find((a) => String(a.id) === String(compressor.assetId));
      return (
        <TableCell key='longitude' className='text-sm text-foreground font-normal py-3 px-4 font-mono text-sm'>
          {asset?.longitude != null ? asset.longitude : "-"}
        </TableCell>
      );
    },
  },
  // Actions column — always locked/visible
  {
    key: "actions",
    label: "Actions",
    locked: true,
    defaultVisible: true,
    renderHead: () => (
      <TableHead key='actions' className={cardStyles.tableHead}>
        Actions
      </TableHead>
    ),
    renderCell: (compressor, ctx) => (
      <TableCell key='actions' className='py-2 px-4'>
        <div className='flex flex-wrap gap-1 sm:gap-2'>
          <Button
            variant='outline'
            size='sm'
            className={outlineChromeButton}
            onClick={(e) => {
              e.stopPropagation();
              ctx.onRowClick?.(compressor.assetId);
            }}>
            <Eye className='h-4 w-4 mr-1' />
            <span className='hidden sm:inline'>View</span>
          </Button>
        </div>
      </TableCell>
    ),
  },
];

/** @type {string[]} Default visible column keys for the downtime table */
export const DOWNTIME_DEFAULT_VISIBLE_KEYS = DOWNTIME_COLUMNS.filter(
  (c) => c.defaultVisible
).map((c) => c.key);

/** @type {string[]} All available column keys for the downtime table */
export const DOWNTIME_ALL_KEYS = DOWNTIME_COLUMNS.map((c) => c.key);

/**
 * Downtime compressor table component
 * @param {Object} props - Component props
 * @param {Array} props.compressors - Array of downtime compressor objects to display
 * @param {Function} props.onRowClick - Callback when a row is clicked, receives assetId
 * @param {string|null} props.sortColumn - Currently active sort column key
 * @param {'asc'|'desc'} props.sortDirection - Current sort direction
 * @param {Function} props.onSort - Callback when a sortable column header is clicked
 * @param {Function} props.getSortIcon - Returns a sort icon element for a given column key
 * @param {string} props.selectedDateRange - Selected date range identifier (e.g. '30d', 'custom')
 * @param {string} props.customStartDate - Custom range start date string (YYYY-MM-DD)
 * @param {string} props.customEndDate - Custom range end date string (YYYY-MM-DD)
 * @param {string[]} [props.visibleKeys] - Override which column keys are visible; defaults to DOWNTIME_DEFAULT_VISIBLE_KEYS
 * @param {boolean} [props.noDataStatsLoading] - Whether no-data stats are loading
 * @returns {JSX.Element} Downtime compressor table
 */
export function CompressorTable({
  compressors,
  onRowClick,
  sortColumn,
  sortDirection,
  onSort,
  getSortIcon,
  selectedDateRange,
  customStartDate,
  customEndDate,
  visibleKeys,
  noDataStatsLoading = false,
}) {
  const { assets } = useAssets();

  const getLatestEvent = (events) => {
    if (events.length === 0) return null;
    const sorted = [...events].sort(
      (a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime()
    );
    return sorted[0];
  };

  /**
   * Generate Downtime percentage
   * @param {number} downtimeHours - Total downtime hours
   * @returns {string} Runtime percentage (0.0-100.0) as string
   */
  const displayRuntimePercentage = (downtimeHours) => {
    let startDate, endDate;

    if (selectedDateRange === "custom" && customStartDate && customEndDate) {
      startDate = new Date(customStartDate + "T00:00:00.000Z");
      endDate = new Date(customEndDate + "T23:59:59.999Z");
    } else if (selectedDateRange && selectedDateRange !== "custom") {
      endDate = new Date();
      startDate = getDateOffset(endDate.toISOString(), selectedDateRange);
    } else {
      endDate = new Date();
      startDate = getDateOffset(endDate.toISOString(), "30d");
    }

    return calculateRuntimePercentage(
      downtimeHours,
      startDate,
      endDate
    ).toFixed(1);
  };

  /**
   * Generate mechanical availability percentage
   * @param {number} mechanicalDTHours - Mechanical downtime hours
   * @returns {string} Mechanical availability percentage (0.0-100.0) as string
   */
  const displayMechanicalAvailabilityPercentage = (mechanicalDTHours) => {
    let startDate, endDate;

    if (selectedDateRange === "custom" && customStartDate && customEndDate) {
      startDate = new Date(customStartDate + "T00:00:00.000Z");
      endDate = new Date(customEndDate + "T23:59:59.999Z");
    } else if (selectedDateRange && selectedDateRange !== "custom") {
      endDate = new Date();
      startDate = getDateOffset(endDate.toISOString(), selectedDateRange);
    } else {
      endDate = new Date();
      startDate = getDateOffset(endDate.toISOString(), "30d");
    }

    return calculateMechanicalAvailabilityPercentage(
      mechanicalDTHours,
      startDate,
      endDate
    ).toFixed(1);
  };

  const ctx = {
    onSort,
    getSortIcon,
    onRowClick,
    assets,
    getLatestEvent,
    displayRuntimePercentage,
    displayMechanicalAvailabilityPercentage,
    noDataStatsLoading,
  };

  // Columns to render: locked ones always included, others filtered by visibleKeys
  const effectiveVisible = visibleKeys ?? DOWNTIME_DEFAULT_VISIBLE_KEYS;
  const visibleColumns = DOWNTIME_COLUMNS.filter(
    (col) => col.locked || effectiveVisible.includes(col.key)
  );

  return (
    <div
      className={cn(
        cardStyles.tableContainer,
        cardStyles.tableFillBody
      )}>
      <Table className='w-full'>
        <TableHeader className={cardStyles.tableHeader}>
          <TableRow className={cardStyles.tableRow}>
            {visibleColumns.map((col) => col.renderHead(ctx))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {compressors.map((compressor, index) => (
            <TableRow
              key={compressor.assetId ?? `downtime-row-${index}`}
              className={cn(cardStyles.bodyRow, "cursor-pointer")}
              onClick={() => onRowClick?.(compressor.assetId)}
              data-testid={`row-compressor-${compressor.assetNumber}`}>
              {visibleColumns.map((col) => col.renderCell(compressor, ctx))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
